/* eslint-disable no-console */
import { sendErrorToServer } from '../sendError';

import { ConsoleCaptureLevel, ExtremeLoggerConfig } from './types';

const ALLOWED_CONSOLE_LEVELS: ConsoleCaptureLevel[] = [
  'debug',
  'info',
  'log',
  'warn',
  'error',
];
const REPORT_WINDOW_MS = 60_000;
const DEFAULT_REPORTS_PER_MINUTE = 5;
const MAX_MESSAGE_LENGTH = 4000;
const MAX_REPORTS_PER_MINUTE = 60;
const TRUNCATION_SUFFIX = '... [truncated]';

type ConsoleMethod = (...args: unknown[]) => void;
type NormalizedExtremeLoggerConfig = Required<ExtremeLoggerConfig>;

const originalConsoleMethods = new Map<ConsoleCaptureLevel, ConsoleMethod>();
const wrappedConsoleMethods = new Map<ConsoleCaptureLevel, ConsoleMethod>();

let activeConfig: NormalizedExtremeLoggerConfig | null = null;
let sentReportTimes: number[] = [];
let isForwardingConsoleReport = false;

export function configureExtremeLogger(config?: ExtremeLoggerConfig): void {
  if (!config) {
    activeConfig = null;
    sentReportTimes = [];
    restoreConsoleMethods();

    return;
  }

  const nextConfig = normalizeConfig(config);

  if (!nextConfig.captureConsole) {
    activeConfig = null;
    sentReportTimes = [];
    restoreConsoleMethods();

    return;
  }

  activeConfig = nextConfig;
  syncConsoleWrappers(nextConfig.consoleLevels);
}

function normalizeConfig(
  config: ExtremeLoggerConfig
): NormalizedExtremeLoggerConfig {
  const consoleLevels = (config.consoleLevels ?? ['warn', 'error'])
    .map(level => String(level).toLowerCase())
    .filter((level): level is ConsoleCaptureLevel =>
      ALLOWED_CONSOLE_LEVELS.includes(level as ConsoleCaptureLevel)
    );

  return {
    captureConsole: config.captureConsole === true,
    consoleLevels: Array.from(new Set(consoleLevels)),
    maxConsoleReportsPerMinute: normalizeMaxReportsPerMinute(
      config.maxConsoleReportsPerMinute
    ),
  };
}

function normalizeMaxReportsPerMinute(value: unknown): number {
  if (value === undefined || value === null) return DEFAULT_REPORTS_PER_MINUTE;

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return 0;

  return Math.min(
    MAX_REPORTS_PER_MINUTE,
    Math.max(0, Math.floor(numericValue))
  );
}

function syncConsoleWrappers(levels: ConsoleCaptureLevel[]): void {
  for (const level of ALLOWED_CONSOLE_LEVELS) {
    if (levels.includes(level)) installConsoleWrapper(level);
    else restoreConsoleMethod(level);
  }
}

function installConsoleWrapper(level: ConsoleCaptureLevel): void {
  if (wrappedConsoleMethods.has(level)) return;

  const original = console[level];

  if (typeof original !== 'function') return;

  originalConsoleMethods.set(level, original as ConsoleMethod);

  const wrapped: ConsoleMethod = (...args: unknown[]) => {
    original.apply(console, args);
    forwardConsoleReport(level, args);
  };

  wrappedConsoleMethods.set(level, wrapped);
  console[level] = wrapped as typeof console[typeof level];
}

function restoreConsoleMethods(): void {
  for (const level of ALLOWED_CONSOLE_LEVELS) {
    restoreConsoleMethod(level);
  }
}

function restoreConsoleMethod(level: ConsoleCaptureLevel): void {
  const wrapped = wrappedConsoleMethods.get(level);
  const original = originalConsoleMethods.get(level);

  if (wrapped && console[level] === wrapped && original) {
    console[level] = original as typeof console[typeof level];
  }

  wrappedConsoleMethods.delete(level);
  originalConsoleMethods.delete(level);
}

function forwardConsoleReport(
  level: ConsoleCaptureLevel,
  args: unknown[]
): void {
  if (!activeConfig || isForwardingConsoleReport) return;

  if (!activeConfig.consoleLevels.includes(level)) return;

  if (!tryConsumeReportSlot(activeConfig.maxConsoleReportsPerMinute)) return;

  isForwardingConsoleReport = true;

  try {
    void sendErrorToServer(
      `[ExtremeLogger][console.${level}] ${serializeConsoleArgs(args)}`
    );
  } finally {
    isForwardingConsoleReport = false;
  }
}

function tryConsumeReportSlot(maxReportsPerMinute: number): boolean {
  if (maxReportsPerMinute <= 0) return false;

  const now = Date.now();

  sentReportTimes = sentReportTimes.filter(
    reportTime => now - reportTime < REPORT_WINDOW_MS
  );

  if (sentReportTimes.length >= maxReportsPerMinute) return false;

  sentReportTimes.push(now);

  return true;
}

function serializeConsoleArgs(args: unknown[]): string {
  const message = args.map(arg => serializeConsoleValue(arg)).join(' ');

  if (message.length <= MAX_MESSAGE_LENGTH) return message;

  return `${message.slice(
    0,
    MAX_MESSAGE_LENGTH - TRUNCATION_SUFFIX.length
  )}${TRUNCATION_SUFFIX}`;
}

function serializeConsoleValue(value: unknown): string {
  if (value instanceof Error) {
    return value.stack || `${value.name}: ${value.message}`;
  }

  if (typeof value === 'string') return value;

  if (value === undefined) return 'undefined';

  try {
    const serialized = JSON.stringify(value, getCircularReplacer());

    return serialized === undefined ? String(value) : serialized;
  } catch {
    return String(value);
  }
}

function getCircularReplacer(): (key: string, value: unknown) => unknown {
  // eslint-disable-next-line @typescript-eslint/ban-types
  const seen = new WeakSet<object>();

  return (_key: string, value: unknown): unknown => {
    if (typeof value !== 'object' || value === null) return value;

    if (seen.has(value)) return '[Circular]';

    seen.add(value);

    return value;
  };
}
